import { PaymentService, notificationService } from '@/api'
import { balanceService } from '@/api/balance'
import BankAccountIcon from '@/assets/icons/bank_account.svg'
import HistoryIcon from '@/assets/icons/history.svg'
import Rank from '@/assets/icons/rank.svg'
import UserProfile from '@/assets/icons/user-profile.svg'
import { REFETCH_INTERVAL } from '@/constants'
import { useContactMap, useContactSync } from '@/hooks'
import { IUser } from '@/interface'
import { popup } from '@/popup_handler'
import { useAppStore } from '@/store'
import { CopyButton, DP } from '@/ui'
import AnnouncementToast from '@/ui/AnnouncementToast'
import { SHOW, handleInvite, handleQrScan, heroBalance, localizeFiat, queryClient, tryOnce } from '@/utils'
import { useQuery } from '@tanstack/react-query'
import { AtSign, Bell, ChevronRight, Download, Megaphone, Plus, QrCode, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ShowMoreList from './components/show_more_list'

const MODULES = [
  // {
  //   name: 'Scan QR code',
  //   icon: <QrCode />,
  //   onClick: handleQrScan,
  // },
  {
    name: 'Receive',
    icon: <Download />,
    onClick: () => popup.open('deposit_from_eoa'),
  },
  {
    name: 'Pay Anyone',
    icon: <AtSign />,
    to: '/dashboard/send-anyone',
  },

  // {
  //   name: 'Connect WC',
  //   icon: <img src={WalletConnectIcon} />,
  //   onClick: handleQrScan,
  // },
  // {
  //   name: 'Pay Contact',
  //   icon: <Contact />,
  //   to: '/dashboard/search/',
  // },
  // {
  //   name: 'Pay CPI-ID',
  //   icon: <Send />,
  //   // onClick: () => popup.open("send_by_cpi_id"),
  //   to: '/dashboard/send-to-cpi',
  // },
  // {
  //   name: 'Pay Address',
  //   icon: <Wallet />,
  //   // onClick: () => popup.open("send_by_ens"),
  //   to: '/dashboard/send-to-address',
  // },
  {
    name: 'Topup',
    icon: <Plus />,
    to: '/dashboard/topup',
  },
  {
    name: 'Invite Friends',
    icon: <Megaphone />,
    onClick: handleInvite,
  },
  // {
  //   name: 'Gift Card',
  //   icon: <Gift />,
  //   to: '/dashboard/gift-card',
  // },
]
const Homepage = () => {
  const [copyButtonClickCount, setCopyButtonClickCount] = useState(0)
  const { wallet, network, loadWallets } = useAppStore((state) => state) as any
  const notifications = useQuery(['notifications'], () => notificationService.GetAll())
  const user = queryClient.getQueryData(['user']) as IUser
  const balance = useQuery(['balance', wallet?._id, network], () => balanceService.Get({ address: wallet?.address }), {
    refetchInterval: REFETCH_INTERVAL,
  })

  const chainBalance = useMemo(() => {
    if (!balance.data) return
    return balance.data?.result.reduce((a: any, b: any) => a + parseFloat(b.usdBalance), 0)
  }, [balance.data?.result, user.default_chain])
  const contactMap = useContactMap()

  const navigation = useNavigate()
  const recentActivity = useQuery(['recentActivity'], () => PaymentService.RecentActivity(), {
    refetchInterval: REFETCH_INTERVAL,
  })
  const recent = useMemo(() => {
    if (!recentActivity.data) return []
    return recentActivity.data.result.users
  }, [recentActivity.data])
  const contactsList = useMemo(() => {
    if (!useContactMap) return []
    return Object.values(contactMap)
  }, [contactMap])

  useContactSync()
  useEffect(() => {
    if (!wallet) return
    const isSkipBackup = localStorage.getItem(`skip_backup:${wallet._id}`)
    if (!wallet?.backup && recentActivity.data?.result?.backupRequired && wallet?.is_local && isSkipBackup !== 'true') {
      popup.open('backup_reminder')
    } else if (wallet?.backup && !wallet?.is_local && !wallet.mnemonicCipher) {
      popup.open('recovery_reminder')
    }
  }, [wallet, recentActivity.data])
  useEffect(() => {
    if (wallet && !wallet?.giftClaimed) {
      tryOnce(
        'gift',
        () =>
          popup.open('gift', {
            title: `Welcome ${user.name}! You\'ve got ${localizeFiat('0.30')} from us to try your first payment.`,
            description: 'Invite friends, spend, and you both keep earning rewards.',
            loadWallets,
          }),
        1,
        'day'
      )
    }
  }, [wallet])
  const texts = ['Pay to contact', 'Pay anyone on Cray', 'Pay to address or ENS']

  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length)
    }, 3000)

    return () => clearInterval(interval) // Cleanup the interval on component unmount
  }, [texts.length])
  useEffect(() => {
    if (copyButtonClickCount < 6) return
    setCopyButtonClickCount(0)
    popup.open('offramp')
    // navigation(`/dashboard/gift-card`)
  }, [copyButtonClickCount])
  const handleSend = async (user: any) => {
    if (user.address) {
      navigation(`/dashboard/chat/${user.address}`)
    } else {
      const userData = await PaymentService.GetUserAddressByCPID(user.username)

      navigation(`/dashboard/chat/${userData.result.address}`)
    }
  }
  const MAX_TILES = 8

  return (
    <div className={`relative flex flex-col h-full w-full`}>
      <div className="flex flex-col overflow-auto h-full overscrollBehaviorY-none">
        <div className="flex items-center justify-between gap-3 px-4 pt-3">
          <Link to="/dashboard/settings" className="h-[48px] w-[48px] shrink-0 rounded-full flex items-center justify-center text-primary active:opacity-90">
            <DP name={user.name} size="36px" className=" text-[18px]" />
          </Link>
          <Link
            to="/dashboard/search"
            className="flex-1 flex items-center gap-2 h-11 px-4 rounded-2xl
          bg-gradient-to-r from-[#FFF1E8] to-[#FFE6D6]
          ring-1 ring-orange-200/60 shadow-sm
          active:shadow-inner active:scale-[0.98] transition-all duration-150"
          >
            <Search className="h-5 w-5 text-[#f96900]" />
            <span className="text-[14px] font-medium text-neutral-900 whitespace-nowrap truncate">{texts[currentIndex]}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/dashboard/notifications/"
              className="relative grid place-items-center h-8 w-8 rounded-2xl
            bg-white ring-1 ring-gray-200 shadow-sm
            text-neutral-700
            active:scale-[0.96] active:shadow-inner transition-all duration-150"
            >
              <Bell className="h-5 w-5" />

              <div {...SHOW(notifications.data?.result.length)} className="w-2 h-2 bg-[#f27821] absolute rounded-full top-[5px] right-[5px] animate-pulse" />
            </Link>
            <button
              onClick={() => handleQrScan({ navigation })}
              aria-label="Scan QR"
              className="
            grid place-items-center h-8 w-8 rounded-2xl
            bg-white ring-1 ring-orange-200/70 shadow-sm
            text-[#f96900]
            active:scale-[0.96] active:shadow-inner transition-all duration-150
          "
            >
              <QrCode className="h-5 w-5" />
            </button>
          </div>
          {/* </div> */}
        </div>
        <div className="">
          <img src="/cray-hero.png" className="  z-[-1] w-full h-[200px] object-cover" />
          <div className="rounded-xl mx-4 mt-2 mb-4 py-3 px-4 flex flex-col items-center text-center shadow-sm bg-[#FFF4ED] ring-1 ring-orange-100">
            <p className="text-sm text-[#555] font-medium">My Balance</p>
            <span className="text-2xl font-bold text-[#222] mt-1">{heroBalance(chainBalance)['localValue']}</span>
            {heroBalance(chainBalance)['usdValue'] && <span className="text-xs text-[#222]/70 mt-0.5">≈ {heroBalance(chainBalance)['usdValue']}</span>}
          </div>
        </div>
        <div className="flex flex-col  w-full px-4 gap-4 flex-grow">
          <div className="grid grid-cols-4">
            {MODULES.map((module) =>
              module.onClick ? (
                <button
                  key={module.name}
                  onClick={() => module.onClick()}
                  className="flex flex-col items-center justify-start w-20 gap-2 text-primary md:hover:bg-gray-100 rounded-[16px] p-2 active:opacity-70 transition-all duration-100"
                >
                  <div className="h-8 w-8 flex items-center justify-center">{module.icon}</div>
                  <span className="inline leading-4 text-center text-foreground text-sm font-medium">{module.name}</span>
                </button>
              ) : (
                <Link
                  key={module.name}
                  to={module.to}
                  className="flex flex-col items-center justify-start w-20 gap-2 text-primary md:hover:bg-gray-100 rounded-[16px] p-2 active:opacity-70 transition-all duration-100"
                >
                  <div className="h-8 w-8  flex items-center justify-center">{module.icon}</div>
                  <span className="inline leading-4 text-center text-foreground text-sm font-medium">{module.name}</span>
                </Link>
              )
            )}
          </div>
          <div className="mx-auto px-5 py-[6px] border-black border rounded-[16px] bg-[#FCF4EF] flex gap-2">
            Cray ID: {user.username}@cray <CopyButton onClick={() => setCopyButtonClickCount((state) => state + 1)} value={`${user.username}@cray`} iconClass="text-[#424242]" />
          </div>
          <div className="flex flex-col gap-4">
            <div {...SHOW(!!recent.length)} className="flex flex-col">
              <p className="text-foreground text-base font-medium leading-tight">Recent</p>
              <ShowMoreList
                items={recent}
                maxItems={MAX_TILES}
                renderItem={(user) => (
                  <button
                    key={user.username}
                    onClick={() => handleSend(user)}
                    className="relative flex flex-col items-center justify-start w-20 gap-2 mt-4 text-primary md:hover:bg-gray-100 rounded-[16px] p-2 active:opacity-70 transition-all duration-100"
                  >
                    <div {...SHOW(user.read_receipt === 'pending')} className="absolute bg-primary w-4 h-4 rounded-full top-1 right-1"></div>
                    {user.name ? (
                      <DP name={user.name} size="52px" className="text-[32px] border border-[#444444]" />
                    ) : (
                      <img className="h-12 w-12 rounded-full object-cover border border-[#444444]" src={UserProfile} />
                    )}
                    <span className="inline leading-4 text-center font-medium text-foreground text-sm h-4 truncate w-16">{user.name || 'Unknown'}</span>
                  </button>
                )}
              />
            </div>
            <div {...SHOW(!!contactsList.length)} className="flex flex-col">
              <p className="text-foreground text-base font-medium leading-tight">People</p>
              <ShowMoreList
                items={contactsList}
                maxItems={MAX_TILES}
                renderItem={(user) => (
                  <button
                    key={user.username}
                    onClick={() => handleSend(user)}
                    className="relative flex flex-col items-center justify-start w-20 gap-2 mt-4 text-primary md:hover:bg-gray-100 rounded-[16px] p-2 active:opacity-70 transition-all duration-100"
                  >
                    <div {...SHOW(user.read_receipt === 'pending')} className="absolute bg-primary w-4 h-4 rounded-full top-1 right-1"></div>
                    {user.name ? (
                      <DP name={user.name} size="52px" className="text-[32px] border border-[#444444]" />
                    ) : (
                      <img className="h-12 w-12 rounded-full object-cover border border-[#444444]" src={UserProfile} />
                    )}
                    <span className="inline leading-4 text-center font-medium text-foreground text-sm h-4 truncate w-16">{user.name || 'Unknown'}</span>
                  </button>
                )}
              />
            </div>

            <div className="flex flex-col gap-6">
              <div className="flex flex-col">
                <Link to="transactions" className="flex gap-4 items-center">
                  <div className="flex items-center flex-grow gap-4">
                    <img src={HistoryIcon} />
                    <span className="text-black">Show transaction history</span>
                  </div>
                  <ChevronRight />
                </Link>
              </div>
              <div className="flex flex-col">
                <Link to="balance" className="flex gap-4 items-center">
                  <div className="flex items-center flex-grow gap-4">
                    <img src={BankAccountIcon} />
                    <span className="text-black">Check account balance</span>
                  </div>
                  <ChevronRight />
                </Link>
              </div>
              <div className="flex flex-col">
                <Link to="Leaderboard" className="flex gap-4 items-center">
                  <div className="flex items-center flex-grow gap-4">
                    <img src={Rank} />
                    <span className="text-black">Leaderboard</span>
                  </div>
                  <ChevronRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 bg-[#FFF3EC] py-6 px-4 rounded-t-2xl mt-12">
          <p className="leading-tight text-l font-semibold">Invite friends. Unlock bonuses.</p>
          <p className="text-sm text-neutral-600 mt-1">You’ll both get a bonus when they make their first payment.</p>
          {/* <p className="text-gray-500 flex gap-2"> */}
          <div className=" flex gap-1">
            <span className="flex  gap-4 items-center px-3 py-2 rounded-xl bg-white ring-1 ring-neutral-200 font-mono text-sm">
              {user.rewardProfile.referralCode}
              <CopyButton value={user.rewardProfile.referralCode} />
            </span>
            <button onClick={handleInvite} className="ml-auto px-4 py-2 rounded-xl bg-[#f96900] text-white text-sm font-semibold">
              Invite a friend
            </button>
          </div>
          {/* </p> */}
        </div>
      </div>

      {wallet && !wallet?.is_local ? (
        <div className="bg-red-500 text-white top-0  w-full left-0 text-center  py-2 z-10">
          This account is not available! <br />{' '}
          <button onClick={() => popup.open('recovery_reminder')} className="underline font-black">
            Recover Now
          </button>
        </div>
      ) : null}
      <AnnouncementToast title="Points Update" body="2600 points have been distributed to the community for last week’s contributions." campaignId="points-alloc-2025w54" />
    </div>
  )
}

export default Homepage
